/**
 * DOM Event Spy
 *
 * Captures actual DOM interactions (clicks, inputs, selects) at the document level
 * and generates Playwright-compatible selectors. When a machine event fires,
 * the captured DOM events are flushed and attached to the trace.
 *
 * This gives us REAL DOM interactions, not inferences from form data.
 */

// ============================================================================
// Types
// ============================================================================

export interface DOMEvent {
  timestamp: number;
  type: "click" | "input" | "change" | "select"; // select is for Radix UI Select options
  selector: string;
  value?: string;
  // Additional context for generating better selectors
  elementInfo: {
    tagName: string;
    role?: string;
    label?: string;
    placeholder?: string;
    name?: string;
    id?: string;
    testId?: string;
    text?: string;
    type?: string; // input type
  };
  // Ancestor context for distinguishing similar elements (e.g., multiple "Book now" buttons)
  ancestorContext?: {
    componentName?: string; // React component name from data-component or fiber
    carouselType?: string; // data-carousel-type (e.g., "featured" or "product")
    productId?: string; // data-product-id
    productName?: string; // data-product-name
    slotIndex?: number; // Position in carousel/list
  };
}

// ============================================================================
// Selector Generation
// ============================================================================

/**
 * Escape quotes in strings for use in Playwright selectors
 * Only straight double quotes need escaping - curly quotes are valid Unicode
 */
function escapeForSelector(str: string): string {
  return str.replace(/\\/g, "\\\\").replace(/"/g, '\\"'); // Only escape straight double quotes
}

/**
 * Generate the best Playwright selector for an element
 * Priority: role > label > placeholder > testId > id > css
 */
function generateSelector(
  element: HTMLElement,
  ancestorContext?: DOMEvent["ancestorContext"],
): string {
  const baseSelector = generateBaseSelector(element);

  // If we have ancestor context with product info, scope the selector
  // This helps distinguish between multiple identical buttons (e.g., multiple "Book now")
  if (ancestorContext?.productId && ancestorContext?.carouselType) {
    // Include both carousel type and product ID for maximum specificity
    return `locator("[data-carousel-type=\\"${escapeForSelector(
      ancestorContext.carouselType,
    )}\\"][data-product-id=\\"${escapeForSelector(
      ancestorContext.productId,
    )}\\"]").${baseSelector}`;
  }

  if (ancestorContext?.productId) {
    return `locator("[data-product-id=\\"${escapeForSelector(
      ancestorContext.productId,
    )}\\"]").${baseSelector}`;
  }

  // Fallback to slot index if available (for carousels without product data)
  if (ancestorContext?.slotIndex !== undefined) {
    return `locator(".keen-slider__slide").nth(${ancestorContext.slotIndex}).${baseSelector}`;
  }

  return baseSelector;
}

/**
 * Generate the base selector without ancestor scoping
 */
function generateBaseSelector(element: HTMLElement): string {
  const tagName = element.tagName.toLowerCase();

  // 1. Role-based selector (best for accessibility)
  const role = element.getAttribute("role") || getImplicitRole(element);
  const accessibleName = getAccessibleName(element);

  if (role && accessibleName) {
    return `getByRole("${role}", { name: "${escapeForSelector(
      accessibleName,
    )}" })`;
  }

  // 2. Label-based selector (for form inputs with proper association)
  const label = getAssociatedLabel(element);
  if (label) {
    return `getByLabel("${escapeForSelector(label)}")`;
  }

  // 3. Visual label + placeholder combo (for inputs in labeled containers without proper for/id)
  // This creates a scoped selector: locator('.space-y-2:has-text("Label")').getByPlaceholder("...")
  const visualLabel = getVisualLabel(element);
  const placeholder = element.getAttribute("placeholder");
  if (visualLabel && placeholder) {
    return `locator(".space-y-2").filter({ hasText: "${escapeForSelector(
      visualLabel,
    )}" }).getByPlaceholder("${escapeForSelector(placeholder)}")`;
  }

  // 4. Placeholder-based selector (fallback if no visual label)
  if (placeholder) {
    return `getByPlaceholder("${escapeForSelector(placeholder)}")`;
  }

  // 5. Test ID selector
  const testId = element.getAttribute("data-testid");
  if (testId) {
    return `getByTestId("${escapeForSelector(testId)}")`;
  }

  // 6. Text content for buttons/links
  if (
    (tagName === "button" || tagName === "a") &&
    element.textContent?.trim()
  ) {
    const text = element.textContent.trim();
    if (text.length < 50) {
      return `getByRole("${role || tagName}", { name: "${escapeForSelector(
        text,
      )}" })`;
    }
  }

  // 7. ID-based selector (fallback)
  const id = element.id;
  if (id) {
    // IDs starting with a digit aren't valid CSS selectors (#1800 is invalid)
    // Use attribute selector instead: [id="1800"]
    if (/^\d/.test(id)) {
      return `locator("[id=\\"${escapeForSelector(id)}\\"]")`;
    }
    return `locator("#${escapeForSelector(id)}")`;
  }

  // 8. CSS selector fallback
  return `locator("${escapeForSelector(buildCssSelector(element))}")`;
}

/**
 * Get implicit ARIA role from element type
 */
function getImplicitRole(element: HTMLElement): string | null {
  const tagName = element.tagName.toLowerCase();
  const type = element.getAttribute("type");

  const roleMap: Record<string, string> = {
    button: "button",
    a: "link",
    input:
      type === "submit"
        ? "button"
        : type === "checkbox"
          ? "checkbox"
          : type === "radio"
            ? "radio"
            : "textbox",
    select: "combobox",
    textarea: "textbox",
    img: "img",
    h1: "heading",
    h2: "heading",
    h3: "heading",
    h4: "heading",
    h5: "heading",
    h6: "heading",
    ul: "list",
    ol: "list",
    li: "listitem",
    nav: "navigation",
    main: "main",
    header: "banner",
    footer: "contentinfo",
    form: "form",
    table: "table",
    dialog: "dialog",
  };

  return roleMap[tagName] || null;
}

/**
 * Get accessible name for an element
 */
function getAccessibleName(element: HTMLElement): string | null {
  // aria-label
  const ariaLabel = element.getAttribute("aria-label");
  if (ariaLabel) return ariaLabel;

  // aria-labelledby
  const labelledBy = element.getAttribute("aria-labelledby");
  if (labelledBy) {
    const labelElement = document.getElementById(labelledBy);
    if (labelElement) return labelElement.textContent?.trim() || null;
  }

  // For buttons/links, use text content
  const tagName = element.tagName.toLowerCase();
  if (
    tagName === "button" ||
    tagName === "a" ||
    element.getAttribute("role") === "button"
  ) {
    // Get visible text, excluding SVG/icon content
    const visibleText = getVisibleTextContent(element);
    if (visibleText && visibleText.length < 100) return visibleText;

    // Fallback to full textContent
    const text = element.textContent?.trim();
    if (text && text.length < 100) return text;

    // Icon-only buttons: check for lucide/svg icons and infer name
    const iconName = inferIconButtonName(element);
    if (iconName) return iconName;
  }

  // For inputs with value (submit buttons)
  if (tagName === "input" && element.getAttribute("type") === "submit") {
    return (element as HTMLInputElement).value || null;
  }

  return null;
}

/**
 * Get visible text content, excluding SVG and other non-visible elements
 * This is more reliable than textContent which can include hidden text
 *
 * Note: We concatenate without spaces because inline elements like <mark>
 * split a single word into multiple text nodes (e.g., "Ae<mark>tna</mark>").
 * The final result is normalized to collapse any actual whitespace.
 */
function getVisibleTextContent(element: HTMLElement): string {
  const texts: string[] = [];

  function walk(node: Node) {
    if (node.nodeType === Node.TEXT_NODE) {
      // Don't trim here - preserve internal spacing, we'll normalize later
      const text = node.textContent || "";
      if (text) texts.push(text);
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      // Skip SVGs, hidden elements, and sr-only (screen reader only) content
      const tagName = el.tagName.toLowerCase();
      if (tagName === "svg" || tagName === "style" || tagName === "script") {
        return;
      }
      // Skip visually hidden elements (common pattern for icon buttons with sr-only text)
      const className = el.className?.toString?.() || "";
      if (
        className.includes("sr-only") ||
        className.includes("visually-hidden")
      ) {
        return;
      }
      // Recurse into children
      for (const child of el.childNodes) {
        walk(child);
      }
    }
  }

  walk(element);
  // Join without adding spaces, then normalize whitespace (collapse multiple spaces, trim)
  return texts.join("").replace(/\s+/g, " ").trim();
}

/**
 * Infer button name from icon classes (lucide-react icons)
 */
function inferIconButtonName(element: HTMLElement): string | null {
  // Check for SVG with lucide class names
  const svg = element.querySelector("svg");
  if (!svg) return null;

  const svgClass =
    (svg as SVGElement).classList?.toString() ||
    svg.getAttribute("class") ||
    "";

  // Common lucide icon patterns -> semantic names
  const iconMap: Record<string, string> = {
    "lucide-arrow-left": "Back",
    "lucide-arrow-right": "Forward",
    "lucide-chevron-left": "Back",
    "lucide-chevron-right": "Next",
    "lucide-x": "Close",
    "lucide-check": "Confirm",
    "lucide-trash": "Delete",
    "lucide-trash-2": "Delete",
    "lucide-edit": "Edit",
    "lucide-pencil": "Edit",
    "lucide-plus": "Add",
    "lucide-minus": "Remove",
    "lucide-search": "Search",
    "lucide-camera": "Camera",
    "lucide-upload": "Upload",
    "lucide-download": "Download",
    "lucide-refresh-cw": "Refresh",
    "lucide-rotate-ccw": "Retry",
    "lucide-skip-forward": "Skip",
    "lucide-home": "Home",
    "lucide-menu": "Menu",
    "lucide-settings": "Settings",
  };

  for (const [iconClass, name] of Object.entries(iconMap)) {
    if (svgClass.includes(iconClass)) {
      return name;
    }
  }

  // Fallback: try to extract from class name pattern "lucide-xxx"
  const lucideMatch = svgClass.match(/lucide-([a-z-]+)/);
  if (lucideMatch) {
    // Convert kebab-case to Title Case
    return lucideMatch[1]
      .split("-")
      .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }

  return null;
}

/**
 * Get label associated with an input element
 *
 * Only returns a label if Playwright's getByLabel() would actually find the element.
 * Playwright supports:
 * 1. aria-label attribute
 * 2. aria-labelledby pointing to a labelling element
 * 3. <label for="id"> with matching input id
 * 4. Input wrapped inside a <label> element
 *
 * Does NOT work for visual-only labels (sibling label without for/id association)
 */
function getAssociatedLabel(element: HTMLElement): string | null {
  // Check for aria-label first (Playwright supports this)
  const ariaLabel = element.getAttribute("aria-label");
  if (ariaLabel) return ariaLabel;

  // Check for aria-labelledby (Playwright supports this)
  const labelledBy = element.getAttribute("aria-labelledby");
  if (labelledBy) {
    const labelElement = document.getElementById(labelledBy);
    if (labelElement) {
      const text = labelElement.textContent?.replace(/\*/g, "").trim();
      if (text) return text;
    }
  }

  // Check for explicit label via for attribute (Playwright supports this)
  const id = element.id;
  if (id) {
    const label = document.querySelector(`label[for="${id}"]`);
    if (label) {
      const text = label.textContent?.replace(/\*/g, "").trim();
      if (text) return text;
    }
  }

  // Check for wrapping label (Playwright supports this)
  const parentLabel = element.closest("label");
  if (parentLabel) {
    // Get text content excluding the input itself
    const clone = parentLabel.cloneNode(true) as HTMLElement;
    const inputs = clone.querySelectorAll("input, select, textarea");
    for (const input of inputs) {
      input.remove();
    }
    const text = clone.textContent?.replace(/\*/g, "").trim();
    if (text) return text;
  }

  // NOTE: Visual-only labels (sibling in .space-y-2 container) are NOT supported
  // by Playwright's getByLabel() - they require proper for/id association.
  // Fall through to placeholder or other selectors instead.

  return null;
}

/**
 * Get visual label text from a sibling label in a container
 * This is for labels that are visually associated but not programmatically linked.
 * Used to create scoped selectors like: locator(".space-y-2").filter({ hasText: "Payer" })
 */
function getVisualLabel(element: HTMLElement): string | null {
  // Check for preceding label sibling in the same container (common Kiosk pattern)
  // e.g., <div class="space-y-2"><KioskLabel>Payer</KioskLabel><input/></div>
  const container = element.closest(".space-y-2, .space-y-1, [data-field]");
  if (container) {
    const label = container.querySelector("label");
    if (label) {
      // Get the label text, stripping asterisks and whitespace
      const labelText = label.textContent?.replace(/\*/g, "").trim();
      if (labelText) return labelText;
    }
  }

  return null;
}

/**
 * Build a CSS selector for an element
 */
function buildCssSelector(element: HTMLElement): string {
  const parts: string[] = [];
  let current: HTMLElement | null = element;

  while (current && current !== document.body) {
    let selector = current.tagName.toLowerCase();

    if (current.id) {
      selector = `#${current.id}`;
      parts.unshift(selector);
      break;
    }

    const className = current.className;
    if (className && typeof className === "string") {
      const classes = className
        .split(/\s+/)
        .filter((c) => c && !c.startsWith("_"));
      if (classes.length > 0) {
        selector += `.${classes.slice(0, 2).join(".")}`;
      }
    }

    // Add nth-child if needed for uniqueness
    const parent = current.parentElement;
    if (parent) {
      const currentElement = current; // Capture non-null value for closure
      const siblings = Array.from(parent.children).filter(
        (c) => c.tagName === currentElement.tagName,
      );
      if (siblings.length > 1) {
        const index = siblings.indexOf(currentElement) + 1;
        selector += `:nth-child(${index})`;
      }
    }

    parts.unshift(selector);
    current = current.parentElement;

    // Limit depth
    if (parts.length > 4) break;
  }

  return parts.join(" > ");
}

// ============================================================================
// DOM Event Spy Class
// ============================================================================

class DOMEventSpy {
  private events: DOMEvent[] = [];
  private isListening = false;
  private maxEvents = 100;

  // Bound handlers for cleanup
  private handleClick = this.onClick.bind(this);
  private handleMouseDown = this.onMouseDown.bind(this);
  private handlePointerDown = this.onPointerDown.bind(this);
  private handleInput = this.onInput.bind(this);
  private handleChange = this.onChange.bind(this);

  /**
   * Start listening for DOM events
   */
  start(): void {
    if (this.isListening) {
      return;
    }
    this.isListening = true;

    document.addEventListener("click", this.handleClick, true);
    document.addEventListener("mousedown", this.handleMouseDown, true);
    document.addEventListener("pointerdown", this.handlePointerDown, true);
    document.addEventListener("input", this.handleInput, true);
    document.addEventListener("change", this.handleChange, true);
  }

  /**
   * Stop listening for DOM events
   */
  stop(): void {
    if (!this.isListening) {
      return;
    }
    this.isListening = false;

    document.removeEventListener("click", this.handleClick, true);
    document.removeEventListener("mousedown", this.handleMouseDown, true);
    document.removeEventListener("pointerdown", this.handlePointerDown, true);
    document.removeEventListener("input", this.handleInput, true);
    document.removeEventListener("change", this.handleChange, true);
  }

  /**
   * Flush and return captured events since last flush
   * Also cleans up orphan combobox clicks (dropdown opens without option selection)
   */
  flush(): DOMEvent[] {
    const events = this.cleanupOrphanComboboxClicks([...this.events]);
    this.events = [];
    return events;
  }

  /**
   * Remove combobox clicks that aren't followed by an option selection.
   * This handles the case where user opens a dropdown but clicks outside to dismiss
   * without selecting an option - we don't want to capture that interaction.
   */
  private cleanupOrphanComboboxClicks(events: DOMEvent[]): DOMEvent[] {
    const result: DOMEvent[] = [];

    for (let i = 0; i < events.length; i++) {
      const event = events[i];

      // Check if this is a combobox click
      if (event.elementInfo.role === "combobox" && event.type === "click") {
        // Look for a following "select" event (option selection)
        const hasFollowingSelect = events
          .slice(i + 1)
          .some((e) => e.type === "select" && e.elementInfo.role === "option");

        if (!hasFollowingSelect) {
          continue; // Skip this event
        }
      }

      result.push(event);
    }

    return result;
  }

  /**
   * Get events without flushing (for debugging)
   */
  peek(): DOMEvent[] {
    return [...this.events];
  }

  /**
   * Clear all captured events
   */
  clear(): void {
    this.events = [];
  }

  // ============================================================================
  // Event Handlers
  // ============================================================================

  private onClick(e: MouseEvent): void {
    const rawTarget = e.target as HTMLElement;
    if (!rawTarget) return;

    // Use composedPath() to get the actual target inside shadow DOM
    // This is necessary because events get retargeted when bubbling out of shadow DOM
    const composedPath = e.composedPath();
    const actualTarget = composedPath[0] as HTMLElement;

    // Skip if it's inside the devtool
    if (rawTarget.closest("[data-devtool]")) return;

    // Handle new Google Places PlaceAutocompleteElement dropdown (part="prediction-item")
    // Use composedPath to find elements inside shadow DOM
    let predictionItem: HTMLElement | null = null;
    for (const el of composedPath) {
      if (el instanceof HTMLElement) {
        const part = el.getAttribute("part");
        const role = el.getAttribute("role");
        if (part === "prediction-item" || role === "option") {
          predictionItem = el;
          break;
        }
      }
    }

    if (predictionItem) {
      // Check if this is inside a gmp-place-autocomplete shadow DOM
      const rootNode = predictionItem.getRootNode();
      const isGooglePlacesPrediction =
        rootNode instanceof ShadowRoot &&
        rootNode.host?.tagName?.toLowerCase() === "gmp-place-autocomplete";

      if (isGooglePlacesPrediction) {
        // Extract address text from the prediction item
        const mainText = predictionItem.querySelector(
          '[part="prediction-item-main-text"], .place-autocomplete-element-place-name',
        );
        const detailsText = predictionItem.querySelector(
          ".place-autocomplete-element-place-details",
        );
        const addressText = [
          mainText?.textContent?.trim(),
          detailsText?.textContent?.trim(),
        ]
          .filter(Boolean)
          .join(", ");

        // Use .first() to click the first visible suggestion
        // The exact address text may vary between runs
        this.addEvent({
          timestamp: Date.now(),
          type: "click",
          selector: `locator("gmp-place-autocomplete").getByRole("option").first()`,
          elementInfo: {
            tagName: "li",
            role: "option",
            label: addressText,
            text: addressText,
          },
        });
        return;
      }
    }

    // Handle old Google Places autocomplete dropdown specially (.pac-item)
    const pacItem = rawTarget.closest(".pac-item") as HTMLElement;
    if (pacItem) {
      // Extract the address text from the autocomplete item
      const querySpan = pacItem.querySelector(".pac-item-query");
      const secondarySpan = pacItem.querySelector(
        "span:not(.pac-icon):not(.pac-item-query)",
      );
      const addressText = [
        querySpan?.textContent?.trim(),
        secondarySpan?.textContent?.trim(),
      ]
        .filter(Boolean)
        .join(", ");

      // Generate a click action on the autocomplete suggestion by text
      this.addEvent({
        timestamp: Date.now(),
        type: "click",
        selector: `locator(".pac-item").filter({ hasText: "${escapeForSelector(
          addressText,
        )}" }).first()`,
        elementInfo: {
          tagName: "div",
          role: "option",
          label: addressText,
          text: addressText,
        },
      });
      return;
    }

    // Skip other elements inside pac-container (but not pac-item which we handled above)
    if (rawTarget.closest(".pac-container")) return;

    // Handle pharmacy search results specially
    const pharmacyResult = rawTarget.closest(
      "[data-pharmacy-result]",
    ) as HTMLElement;
    if (pharmacyResult) {
      const pharmacyName =
        pharmacyResult.querySelector(".font-medium")?.textContent?.trim() ||
        "pharmacy";

      this.addEvent({
        timestamp: Date.now(),
        type: "click",
        selector: `locator("[data-pharmacy-result]").first()`,
        elementInfo: {
          tagName: "button",
          role: "option",
          label: pharmacyName,
          text: pharmacyName,
        },
      });
      return;
    }

    // Handle Radix Select options (role="option" inside a Radix select portal)
    const radixOption = rawTarget.closest('[role="option"]') as HTMLElement;
    if (radixOption) {
      // Check if this is inside a Radix select content (has data-radix-collection-item)
      const isRadixSelect =
        radixOption.hasAttribute("data-radix-collection-item") ||
        radixOption.closest("[data-radix-select-viewport]");

      if (isRadixSelect) {
        const optionText = radixOption.textContent?.trim() || "option";

        this.addEvent({
          timestamp: Date.now(),
          type: "select",
          selector: `getByRole("option", { name: "${escapeForSelector(
            optionText,
          )}", exact: true })`,
          value: optionText,
          elementInfo: {
            tagName: "div",
            role: "option",
            label: optionText,
            text: optionText,
          },
        });
        return;
      }
    }

    // Find the actual interactive element (walk up the tree if needed)
    const target = this.findInteractiveElement(rawTarget);

    if (!target) return;

    const ancestorContext = this.getAncestorContext(target);

    this.addEvent({
      timestamp: Date.now(),
      type: "click",
      selector: generateSelector(target, ancestorContext),
      elementInfo: this.getElementInfo(target),
      ancestorContext,
    });
  }

  /**
   * Handle mousedown for special elements:
   * - Google Places autocomplete (uses mousedown, not click)
   * - Signature canvas (capture stroke start)
   */
  private onMouseDown(e: MouseEvent): void {
    const rawTarget = e.target as HTMLElement;
    if (!rawTarget) return;

    // Handle signature canvas
    const signatureCanvas = rawTarget.closest("[data-signature-canvas]");
    if (signatureCanvas && rawTarget.tagName.toLowerCase() === "canvas") {
      // Only capture once per signature (check if we already have a signature event pending)
      const hasSignatureEvent = this.events.some((ev) =>
        ev.selector.includes("[data-signature-canvas]"),
      );
      if (!hasSignatureEvent) {
        this.addEvent({
          timestamp: Date.now(),
          type: "click", // We'll handle this specially in test generation
          selector: `locator("[data-signature-canvas] canvas")`,
          elementInfo: {
            tagName: "canvas",
            role: "img",
            label: "signature canvas",
            text: "signature",
          },
        });
      }
      return;
    }

    // Handle new Google Places PlaceAutocompleteElement dropdown
    // Use composedPath() to find elements inside shadow DOM (events get retargeted)
    const composedPath = e.composedPath();
    let predictionItem: HTMLElement | null = null;
    for (const el of composedPath) {
      if (el instanceof HTMLElement) {
        const part = el.getAttribute("part");
        const role = el.getAttribute("role");
        if (part === "prediction-item" || role === "option") {
          predictionItem = el;
          break;
        }
      }
    }

    if (predictionItem) {
      const rootNode = predictionItem.getRootNode();
      const isGooglePlacesPrediction =
        rootNode instanceof ShadowRoot &&
        rootNode.host?.tagName?.toLowerCase() === "gmp-place-autocomplete";

      if (isGooglePlacesPrediction) {
        const addressText =
          predictionItem.textContent?.trim() || "address suggestion";

        this.addEvent({
          timestamp: Date.now(),
          type: "click",
          selector: `locator("gmp-place-autocomplete").getByRole("option").first()`,
          elementInfo: {
            tagName: "li",
            role: "option",
            label: addressText,
            text: addressText,
          },
        });
        return;
      }
    }

    // Handle old Google Places autocomplete items (.pac-item)
    const pacItem = rawTarget.closest(".pac-item") as HTMLElement;
    if (!pacItem) return;

    // Get the full text content for logging/debugging
    const addressText = pacItem.textContent?.trim() || "address suggestion";

    // Use .first() to click the first visible suggestion
    // The exact address text may vary between runs (Google API responses differ),
    // so we just select the first autocomplete result
    this.addEvent({
      timestamp: Date.now(),
      type: "click",
      selector: `locator(".pac-item").first()`,
      elementInfo: {
        tagName: "div",
        role: "option",
        label: addressText,
        text: addressText,
      },
    });
  }

  /**
   * Handle pointerdown for Radix UI components that use pointer events
   * and stop propagation on click (like Select)
   */
  private onPointerDown(e: PointerEvent): void {
    const rawTarget = e.target as HTMLElement;
    if (!rawTarget) return;

    // Skip if it's inside the devtool
    if (rawTarget.closest("[data-devtool]")) return;

    // Handle Radix Select trigger (combobox)
    const combobox = rawTarget.closest('[role="combobox"]') as HTMLElement;
    if (combobox) {
      // For comboboxes, prefer ID-based selector since accessible name can be unreliable
      // (placeholder vs label association)
      const comboboxId = combobox.id;
      let selector: string;

      if (comboboxId) {
        // Use ID selector - most reliable for Radix Select
        // IDs starting with a digit aren't valid CSS selectors, use attribute selector
        if (/^\d/.test(comboboxId)) {
          selector = `locator("[id=\\"${escapeForSelector(comboboxId)}\\"]")`;
        } else {
          selector = `locator("#${comboboxId}")`;
        }
      } else {
        // Fallback to generic combobox selector
        selector = generateSelector(combobox);
      }

      this.addEvent({
        timestamp: Date.now(),
        type: "click",
        selector,
        elementInfo: this.getElementInfo(combobox),
      });
      return;
    }

    // Handle Radix Select options (role="option")
    const option = rawTarget.closest('[role="option"]') as HTMLElement;
    if (option) {
      // Check if this is inside a Radix select (has data-radix-collection-item)
      const isRadixSelect =
        option.hasAttribute("data-radix-collection-item") ||
        option.closest("[data-radix-select-viewport]");

      if (isRadixSelect) {
        const optionText = option.textContent?.trim() || "option";

        this.addEvent({
          timestamp: Date.now(),
          type: "select",
          selector: `getByRole("option", { name: "${escapeForSelector(
            optionText,
          )}", exact: true })`,
          value: optionText,
          elementInfo: {
            tagName: "div",
            role: "option",
            label: optionText,
            text: optionText,
          },
        });
        return;
      }
    }
  }

  private onInput(e: Event): void {
    const target = e.target as HTMLInputElement | HTMLTextAreaElement;
    if (!target) return;

    // Skip if it's inside the devtool
    if (target.closest("[data-devtool]")) return;

    // Special handling for Google Places autocomplete input (inside shadow DOM)
    // When events bubble out of shadow DOM, the target is retargeted to the host element.
    // We need to check if the target is inside or IS the gmp-place-autocomplete element.
    let selector: string;

    // Check if target is the gmp-place-autocomplete host itself (retargeted from shadow DOM)
    // OR if it's inside one (for composed events)
    const googlePlacesHost =
      target.tagName?.toLowerCase() === "gmp-place-autocomplete"
        ? target
        : target.closest("gmp-place-autocomplete");

    const isGooglePlacesInput = !!googlePlacesHost;

    if (isGooglePlacesInput) {
      // Use locator that pierces shadow DOM to find the combobox input
      selector = `locator("gmp-place-autocomplete").getByRole("combobox")`;
    } else {
      selector = generateSelector(target);
    }

    // Debounce rapid input events - only keep the latest value
    const existingIndex = this.events.findIndex(
      (ev) => ev.type === "input" && ev.selector === selector,
    );

    const event: DOMEvent = {
      timestamp: Date.now(),
      type: "input",
      selector,
      value: target.value,
      elementInfo: this.getElementInfo(target),
    };

    if (existingIndex >= 0) {
      // Update existing event with latest value
      this.events[existingIndex] = event;
    } else {
      this.addEvent(event);
    }
  }

  private onChange(e: Event): void {
    const target = e.target as HTMLSelectElement | HTMLInputElement;
    if (!target) return;

    // Skip if it's inside the devtool
    if (target.closest("[data-devtool]")) return;

    // Skip Radix UI hidden form elements (they use aria-hidden and are visually hidden)
    // Radix Select uses a hidden native <select> for form submission, but we capture
    // the actual interaction via pointerdown on the combobox trigger and option click
    if (target.getAttribute("aria-hidden") === "true") {
      return;
    }
    if (target.closest("[data-radix-select-viewport]")) {
      return;
    }
    // Also skip if there's a Radix select trigger nearby with the same ID
    // (Radix uses a button with role=combobox + hidden select)
    const targetId = target.id;
    if (targetId) {
      try {
        if (
          document.querySelector(
            `button#${CSS.escape(targetId)}[role="combobox"]`,
          )
        ) {
          return;
        }
      } catch {
        // Invalid selector, ignore
      }
    }

    // For selects and checkboxes/radios
    const tagName = target.tagName.toLowerCase();
    if (
      tagName === "select" ||
      target.type === "checkbox" ||
      target.type === "radio"
    ) {
      this.addEvent({
        timestamp: Date.now(),
        type: "change",
        selector: generateSelector(target),
        value: target.value,
        elementInfo: this.getElementInfo(target),
      });
    }
  }

  // ============================================================================
  // Helpers
  // ============================================================================

  private isInteractiveElement(element: HTMLElement): boolean {
    const tagName = element.tagName.toLowerCase();
    const role = element.getAttribute("role");

    // Interactive elements (native HTML)
    const interactiveTags = [
      "button",
      "a",
      "input",
      "select",
      "textarea",
      "option",
    ];
    if (interactiveTags.includes(tagName)) return true;

    // Elements with interactive roles
    const interactiveRoles = [
      "button",
      "link",
      "checkbox",
      "radio",
      "option",
      "menuitem",
      "tab",
      "combobox", // Radix Select trigger
      "listbox", // Radix Select content
    ];
    if (role && interactiveRoles.includes(role)) return true;

    // Don't use cursor: pointer heuristic here - it catches child elements like <p>, <span>, <div>
    // that inherit pointer from parent buttons. We'll only use it as a fallback in findInteractiveElement.
    return false;
  }

  /**
   * Walk up the DOM tree to find the nearest interactive element
   * This handles cases where clicks land on child elements (e.g., <p> inside <button>)
   */
  private findInteractiveElement(element: HTMLElement): HTMLElement | null {
    let current: HTMLElement | null = element;
    let pointerElement: HTMLElement | null = null;

    // Walk up to 10 levels to find an interactive parent
    for (let i = 0; i < 10 && current; i++) {
      // Check for native interactive element first
      if (this.isInteractiveElement(current)) {
        return current;
      }

      // Track the first element with cursor: pointer as fallback
      if (!pointerElement) {
        const style = window.getComputedStyle(current);
        if (style.cursor === "pointer") {
          pointerElement = current;
        }
      }

      current = current.parentElement;
    }

    // If no native interactive element found, use the pointer element as fallback
    return pointerElement;
  }

  private getElementInfo(element: HTMLElement): DOMEvent["elementInfo"] {
    return {
      tagName: element.tagName.toLowerCase(),
      role:
        element.getAttribute("role") || getImplicitRole(element) || undefined,
      label: getAssociatedLabel(element) || undefined,
      placeholder: element.getAttribute("placeholder") || undefined,
      name: element.getAttribute("name") || undefined,
      id: element.id || undefined,
      testId: element.getAttribute("data-testid") || undefined,
      text: element.textContent?.trim().slice(0, 50) || undefined,
      type: element.getAttribute("type") || undefined,
    };
  }

  /**
   * Get ancestor context to help distinguish similar elements (e.g., multiple "Book now" buttons)
   * Walks up the DOM tree looking for:
   * 1. React component names (from React DevTools fiber or data-component attribute)
   * 2. Product-related data attributes
   * 3. Carousel slide index
   */
  private getAncestorContext(
    element: HTMLElement,
  ): DOMEvent["ancestorContext"] {
    const context: DOMEvent["ancestorContext"] = {};
    let current: HTMLElement | null = element;
    let slotIndex: number | undefined;

    // Walk up to 15 levels looking for context
    for (let i = 0; i < 15 && current; i++) {
      // Check for data-component attribute (explicit component marking)
      if (!context.componentName) {
        const componentName = current.getAttribute("data-component");
        if (componentName) {
          context.componentName = componentName;
        }
      }

      // Check for React fiber (internal React property)
      // This gives us the actual React component name
      if (!context.componentName) {
        const fiberKey = Object.keys(current).find(
          (key) =>
            key.startsWith("__reactFiber$") ||
            key.startsWith("__reactInternalInstance$"),
        );
        if (fiberKey) {
          const fiber = (current as unknown as Record<string, unknown>)[
            fiberKey
          ] as {
            return?: { type?: { name?: string; displayName?: string } };
            type?: { name?: string; displayName?: string };
          } | null;
          // Walk up the fiber tree to find a named component
          let fiberNode = fiber;
          for (let j = 0; j < 10 && fiberNode; j++) {
            const type = fiberNode.type || fiberNode.return?.type;
            const name = type?.displayName || type?.name;
            // Skip anonymous/internal components
            if (name && !name.startsWith("_") && name.length > 1) {
              context.componentName = name;
              break;
            }
            fiberNode = fiberNode.return as typeof fiberNode;
          }
        }
      }

      // Check for carousel type (featured vs product)
      if (!context.carouselType) {
        const carouselType = current.getAttribute("data-carousel-type");
        if (carouselType) {
          context.carouselType = carouselType;
        }
      }

      // Check for product-related data attributes
      if (!context.productId) {
        const productId =
          current.getAttribute("data-product-id") ||
          current.getAttribute("data-productid");
        if (productId) {
          context.productId = productId;
        }
      }

      if (!context.productName) {
        const productName =
          current.getAttribute("data-product-name") ||
          current.getAttribute("data-productname");
        if (productName) {
          context.productName = productName;
        }
      }

      // Check for carousel slide (keen-slider uses this class)
      if (
        slotIndex === undefined &&
        current.classList.contains("keen-slider__slide")
      ) {
        const parent = current.parentElement;
        if (parent) {
          const slides = Array.from(
            parent.querySelectorAll(".keen-slider__slide"),
          );
          slotIndex = slides.indexOf(current);
          if (slotIndex >= 0) {
            context.slotIndex = slotIndex;
          }
        }
      }

      current = current.parentElement;
    }

    // Only return if we found something useful
    return Object.keys(context).length > 0 ? context : undefined;
  }

  private addEvent(event: DOMEvent): void {
    // Dedupe consecutive clicks on the same element (spam click prevention)
    // Only dedupe clicks within 500ms of each other
    if (event.type === "click" && this.events.length > 0) {
      const lastEvent = this.events[this.events.length - 1];
      if (
        lastEvent.type === "click" &&
        lastEvent.selector === event.selector &&
        event.timestamp - lastEvent.timestamp < 500
      ) {
        // Skip duplicate click
        return;
      }
    }

    this.events.push(event);

    // Prevent unbounded growth
    if (this.events.length > this.maxEvents) {
      this.events.shift();
    }
  }
}

// ============================================================================
// Singleton Export
// ============================================================================

export const domEventSpy = new DOMEventSpy();

// ============================================================================
// React Hook
// ============================================================================

import { useEffect } from "react";

/**
 * Hook to start/stop DOM event spy
 */
export function useDOMEventSpy(enabled = true) {
  useEffect(() => {
    if (enabled) {
      domEventSpy.start();
    }
    return () => {
      domEventSpy.stop();
    };
  }, [enabled]);

  return {
    flush: domEventSpy.flush.bind(domEventSpy),
    peek: domEventSpy.peek.bind(domEventSpy),
    clear: domEventSpy.clear.bind(domEventSpy),
  };
}
